import { Component, ChangeDetectionStrategy, Inject } from '@angular/core';
import { ActiveSecurityContextStateService } from '@security/active-security/active-security-context.state-service';
import { BurgerBarService } from '../../../burger-bar/burger-bar.service';
import { EmployerStatisticsStateService } from '@app/employers/dashboard/state/employer-statistics.state-service';
import { EmployersStateService } from '@app/employers/employer-select/state/employers-state.service';
import { EmployerStatisticsDataProvider } from '@app/employers/dashboard/state/employer-statistics.data-provider';
import { EmployersDataProvider } from '@app/employers/employer-select/state/employers-data.provider';
import { NavigationLink, TopNavigationLink } from '@app/app-layout/header/product-navigation/navigation-bar/navigation-bar';
import { FeatureFlagService, FeatureFlagsServiceInjectionToken } from '@feature-flags/feature-flag.service';
import { BehaviorSubject, combineLatest, filter, of, take } from 'rxjs';
import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
import { integrationsRoutes } from '@app/integrations-admin/integrations-routes';
import { organisationRoutes } from '@security/organisations/organisation-routes';
import { userRoutes } from '@security/users/user-routes';
import { cintraHrLogRoutes } from '@app/hrlog/cintrahrlog-routes';
import { AppClientTypesStateService } from '@app/integrations-admin/integrations/shared/state/app-client-types/app-client-types.state-service';
import { customReportTypesRoutes } from '@app/custom-reports-admin/custom-report-types-routes';

@UntilDestroy()
@Component({
  selector: 'app-cintra-pay-navigation',
  templateUrl: './cintra-pay-navigation.component.html',
  styleUrls: ['./cintra-pay-navigation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [EmployerStatisticsDataProvider, EmployersDataProvider, EmployerStatisticsStateService, EmployersStateService]
})
export class CintraPayNavigationComponent {
  cintraPayLinksBehaviorSubject: BehaviorSubject<TopNavigationLink[]> = new BehaviorSubject<TopNavigationLink[]>([]);
  cintraPayLinks$ = this.cintraPayLinksBehaviorSubject.asObservable();

  constructor(
    public activeSecurity: ActiveSecurityContextStateService,
    public burgerBarService: BurgerBarService,
    @Inject(FeatureFlagsServiceInjectionToken) public featureFlagService: FeatureFlagService,
    private employersStateService: EmployersStateService,
    private appClientTypeStateService: AppClientTypesStateService
  ) {
    this.createCintraPayLinks();
  }

  createCintraPayLinks = () => {
    this.featureFlagService
      .getAllValues$()
      .pipe(take(1))
      .subscribe((featureFlags) => {
        const payrollLinks: TopNavigationLink[] = [];

        if (this.activeSecurity.hasOrganisationAuthorityTo('AccessPayrolls')) {
          payrollLinks.push({
            label: 'Payrolls',
            route: 'employers',
            feature: 'Payrolls',
            execute: (data?: any) => this.activeSecurity.navigatePayrolls()
          });
        }

        if (this.activeSecurity.hasOrganisationAuthorityTo('AccessPayrollReports')) {
          payrollLinks.push({
            label: 'Reports',
            route: 'payrolls/reports',
            feature: 'Reports'
          });
        }

        if (this.activeSecurity.hasOrganisationAuthorityTo('AccessGroups') && this.activeSecurity.activeMembership.groupsEnabled) {
          payrollLinks.push({
            label: 'Groups',
            route: 'access-control/groups',
            feature: 'Groups'
          });
        }

        if (this.activeSecurity.hasOrganisationAuthorityTo('AccessMessaging') && this.activeSecurity.activeMembership.messagingEnabled) {
          payrollLinks.push({
            label: 'Messaging',
            route: 'messaging/inbox',
            feature: 'Messaging'
          });
        }

        if (
          this.activeSecurity.hasOrganisationAuthorityTo('AccessPayrolls') &&
          this.activeSecurity.activeMembership.cintraIqAuroraEnabled
        ) {
          let cloudIQURL = 'https://cloud.aurora.cintra.co.uk/RDWeb/webclient';

          if (featureFlags['cintraIqInAws'] === true) cloudIQURL = 'https://payrollengine.cintra.co.uk/RDWeb/webclient';

          payrollLinks.push({
            label: 'Cloud IQ',
            route: cloudIQURL,
            openInNewTab: true,
            feature: undefined
          });
        }

        const settings: NavigationLink[] = [];

        if (this.activeSecurity.hasOrganisationAuthorityTo('EditUsers')) {
          settings.push({
            label: 'Manage users',
            route: userRoutes.hub
          });
        }

        if (
          this.activeSecurity.hasFeaturePackAuthorityTo('AccessPaySpines') &&
          this.activeSecurity.hasOrganisationAuthorityTo('AccessPayrolls')
        ) {
          settings.push({
            label: 'Position management',
            route: 'positions/positions-hub'
          });
        }

        if (this.activeSecurity.hasApplicationAuthorityTo('EditCintraEmployees')) {
          settings.push({
            label: 'Manage Cintra users',
            route: 'users/cintra-hub'
          });
        }

        if (this.activeSecurity.hasApplicationAuthorityTo('EditOrganisations')) {
          settings.push(
            {
              label: 'Manage organisations',
              route: organisationRoutes.hub
            },
            {
              label: 'Cintra Admin Area',
              route: 'admin-area/hub'
            }
          );
        }

        if (this.activeSecurity.hasApplicationAuthorityTo('CreateCustomReports')) {
          settings.push({
            label: 'Manage Custom Reports',
            route: customReportTypesRoutes.hub
          });
        }

        if (this.activeSecurity.hasOrganisationAuthorityTo('ManageIntegrations')) {
          settings.push({
            label: 'Manage integrations',
            route: integrationsRoutes.hub
          });
        }

        if (this.activeSecurity.isCintraAdmin()) {
          settings.push({
            label: 'Design system',
            route: 'design/system'
          });
        }

        if (featureFlags['dynamicReporting'] === true && this.activeSecurity.hasApplicationAuthorityTo('ManageReporting')) {
          settings.push({
            label: 'Live reporting',
            route: 'live-reporting/hub'
          });
        }

        if (settings.length > 0) {
          payrollLinks.push({
            label: 'Settings',
            route: '/settings/',
            sectionLinks: settings,
            feature: 'Settings'
          });
        }

        this.createAsyncLinks(featureFlags);

        this.cintraPayLinksBehaviorSubject.next(payrollLinks);
      });
  };

  createAsyncLinks(featureFlags): void {
    let run = false;
    const toResolve = { links: this.cintraPayLinks$, employers: of(undefined) as any, hrEnabled: of(undefined) as any };
    const employersCheck = featureFlags['equifaxOptOut'] === true && this.activeSecurity.hasOrganisationAuthorityTo('EquifaxDataConsent');
    const hrCheck = this.activeSecurity.hasOrganisationAuthorityTo('AccessPayrolls') && featureFlags['cintraHrLog'] === true;

    if (employersCheck) {
      toResolve.employers = this.employersStateService.employers$;
      run = true;
    }

    if (hrCheck) {
      toResolve.hrEnabled = this.appClientTypeStateService.hasHrEnabled$();
      run = true;
    }

    if (!run) return;

    combineLatest(toResolve)
      .pipe(
        untilDestroyed(this),
        filter((r) => {
          const employersResult = employersCheck ? r.employers !== null : true;
          const hrResult = hrCheck ? typeof r.hrEnabled === 'boolean' : true;
          const linkResult = !!r.links && r.links.length > 0;

          return employersResult && hrResult && linkResult;
        }),
        take(1)
      )
      .subscribe((result) => {
        const cintraPayLinksClone = JSON.parse(JSON.stringify(result.links));

        if (employersCheck) {
          let settingsLink = cintraPayLinksClone.find((cintraPayLink) => cintraPayLink.label === 'Settings');

          if (!settingsLink) {
            settingsLink = {
              label: 'Settings',
              route: '/settings/',
              sectionLinks: [],
              feature: 'Settings'
            };
            cintraPayLinksClone.push(settingsLink);
          }

          settingsLink.sectionLinks.push({
            label: 'Organisation settings',
            route: 'organisation/settings'
          });
        }

        if (hrCheck && result.hrEnabled) {
          const hrLink = {
            label: 'CintraHR log',
            route: cintraHrLogRoutes.hub,
            feature: 'CintraHrLog'
          };

          if (cintraPayLinksClone.some((l) => l.label === 'Settings')) {
            cintraPayLinksClone.splice(
              cintraPayLinksClone.findIndex((pl) => pl.label === 'Settings'),
              0,
              hrLink
            );
          } else {
            cintraPayLinksClone.push(hrLink);
          }
        }

        this.cintraPayLinksBehaviorSubject.next(cintraPayLinksClone);
      });
  }
}
